import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  QuoteRequest,
  QuoteResponse,
  QuoteSearchParams,
  QuoteItemRequest,
  AddBundleRequest,
  PageResponse,
  QuotePublicResponse,
  QuotePublicRejectRequest,
  QuotePublicRejectResponse,
  QuoteSignRequest,
  QuoteSignatureResponse,
  QuoteHistoryEntry,
  QuoteMetricsResponse,
  QuoteVersionResponse,
  SendQuoteEmailRequest,
  SendQuoteEmailResponse,
  ResponsibleSignRequest,
  QuoteSignatureView
} from '../models/quote.model';

@Injectable({
  providedIn: 'root'
})
export class QuoteApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) { }

  // GET /api/quotes - List quotes with filters and pagination
  searchQuotes(params: QuoteSearchParams): Observable<PageResponse<QuoteResponse>> {
    let httpParams = new HttpParams()
      .set('businessId', params.businessId.toString());

    if (params.dealId !== undefined) {
      httpParams = httpParams.set('dealId', params.dealId.toString());
    }
    if (params.statusId !== undefined) {
      httpParams = httpParams.set('statusId', params.statusId.toString());
    }
    if (params.dateFrom) {
      httpParams = httpParams.set('dateFrom', params.dateFrom);
    }
    if (params.dateTo) {
      httpParams = httpParams.set('dateTo', params.dateTo);
    }
    if (params.validFrom) {
      httpParams = httpParams.set('validFrom', params.validFrom);
    }
    if (params.validTo) {
      httpParams = httpParams.set('validTo', params.validTo);
    }
    if (params.searchQuoteNumber) {
      httpParams = httpParams.set('searchQuoteNumber', params.searchQuoteNumber);
    }
    if (params.page !== undefined) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', params.size.toString());
    }
    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params.sortDirection) {
      httpParams = httpParams.set('sortDirection', params.sortDirection);
    }

    return this.http.get<PageResponse<QuoteResponse>>(`${this.apiUrl}/quotes`, { params: httpParams });
  }

  // GET /api/quotes/{id} - Get quote by ID
  getQuoteById(id: number, businessId: number): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteResponse>(`${this.apiUrl}/quotes/${id}`, { params });
  }

  // GET /api/quotes/deal/{dealId} - Get all quotes for a deal
  getQuotesByDeal(dealId: number, businessId: number): Observable<QuoteResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteResponse[]>(`${this.apiUrl}/quotes/deal/${dealId}`, { params });
  }

  // POST /api/quotes - Create new quote
  createQuote(quote: QuoteRequest): Observable<QuoteResponse> {
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes`, quote);
  }

  // POST /api/quotes/from-deal/{dealId} - Create quote pre-populated from deal data (NEW)
  createFromDeal(dealId: number, businessId: number): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/from-deal/${dealId}`, null, { params });
  }

  // PUT /api/quotes/{id} - Update existing quote
  updateQuote(id: number, quote: QuoteRequest): Observable<QuoteResponse> {
    return this.http.put<QuoteResponse>(`${this.apiUrl}/quotes/${id}`, quote);
  }

  // DELETE /api/quotes/{id} - Delete quote
  deleteQuote(id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/quotes/${id}`, { params });
  }

  // PUT /api/quotes/{id}/status - Change quote status
  // Status names updated: "Enviada" -> "Publicada", "Aprobada" -> "Aceptada"
  changeStatus(id: number, businessId: number, statusId: number): Observable<QuoteResponse> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('statusId', statusId.toString());
    return this.http.put<QuoteResponse>(`${this.apiUrl}/quotes/${id}/status`, null, { params });
  }

  // POST /api/quotes/{id}/publish - Publish quote, sets status to "Publicada" and emits the public link.
  // The publicUrl/publicPdfUrl/publicToken are revealed ONCE in this response (stored hashed). Sales Rep+
  publishQuote(id: number, businessId: number): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/${id}/publish`, null, { params });
  }

  // POST /api/quotes/{id}/rotate-link - Issues a brand-new public token, invalidating the previous link
  // immediately. The new publicUrl/publicPdfUrl are returned ONCE in this response. Requires Manager+
  rotateLink(id: number, businessId: number): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/${id}/rotate-link`, null, { params });
  }

  // POST /api/quotes/{id}/revoke-link - Clears the public token so the share link stops working
  // immediately. The quote is kept intact and can be re-shared via publish/rotate-link. Requires Manager+
  revokeLink(id: number, businessId: number): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/${id}/revoke-link`, null, { params });
  }

  // POST /api/quotes/{id}/send-email - Send the quote by email to the client (Sales Rep+).
  // Body fully optional: without recipients the backend uses the quote's/deal's primary contact.
  // CADA envío emite un enlace público nuevo (publica si era Borrador, rota el token si ya estaba
  // publicada); el enlace anterior deja de funcionar. El token fresco viene en response.quote.
  // El envío real es asíncrono (outbox) — emailMessageId es el id del mensaje encolado.
  sendEmail(id: number, businessId: number, request?: SendQuoteEmailRequest): Observable<SendQuoteEmailResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<SendQuoteEmailResponse>(`${this.apiUrl}/quotes/${id}/send-email`, request ?? {}, { params });
  }

  // POST /api/quotes/{id}/responsible-sign - Estampa la firma del responsable (signer_type=APPROVER)
  // en el documento (Sales Rep+). Body OPCIONAL: si trae signatureData usa esa firma; si va vacío,
  // usa la firma registrada del quote_approver del usuario actual. 422 si no hay firma en body y el
  // usuario no tiene firma registrada. Devuelve la firma estampada (QuoteSignatureView).
  responsibleSign(id: number, businessId: number, request?: ResponsibleSignRequest): Observable<QuoteSignatureView> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteSignatureView>(`${this.apiUrl}/quotes/${id}/responsible-sign`, request ?? {}, { params });
  }

  // PUT /api/quotes/{id}/calculate - Recalculate totals using taxEnabled/taxRate from quote
  calculateTotals(id: number, businessId: number): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.put<QuoteResponse>(`${this.apiUrl}/quotes/${id}/calculate`, null, { params });
  }

  // POST /api/quotes/{id}/new-version - Create new version of quote
  // versionNotes is optional (new in v2.0)
  createNewVersion(id: number, businessId: number, versionNotes?: string): Observable<QuoteResponse> {
    let params = new HttpParams().set('businessId', businessId.toString());
    if (versionNotes) {
      params = params.set('versionNotes', versionNotes);
    }
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/${id}/new-version`, null, { params });
  }

  // GET /api/quotes/{id}/versions - Version history of the quote family (same quoteNumber),
  // ordered by version DESC. isCurrent=true marks the only non-"Reemplazada" version.
  getVersions(id: number, businessId: number): Observable<QuoteVersionResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteVersionResponse[]>(`${this.apiUrl}/quotes/${id}/versions`, { params });
  }

  // POST /api/quotes/{id}/restore - Restore a historical version: creates a new draft version
  // cloning that one and moves the current one to "Reemplazada". {id} is the historical version's id.
  restoreVersion(historicalId: number, businessId: number): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/${historicalId}/restore`, null, { params });
  }

  // POST /api/quotes/{id}/duplicate - Duplicate as a NEW independent quote (new number, Borrador, v1).
  // Copies items and validity (validUntil/datetimeExpires); resets lifecycle timestamps. Returns 201.
  duplicateQuote(id: number, businessId: number): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/${id}/duplicate`, null, { params });
  }

  // POST /api/quotes/{id}/items - Add item to quote
  addItem(id: number, businessId: number, item: QuoteItemRequest): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/${id}/items`, item, { params });
  }

  // POST /api/quotes/{id}/bundle-items - Add bundle to quote
  addBundleItem(quoteId: number, businessId: number, request: AddBundleRequest): Observable<QuoteResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteResponse>(`${this.apiUrl}/quotes/${quoteId}/bundle-items`, request, { params });
  }

  // GET /api/quotes/{id}/history - Audit history (requires Manager+) (NEW)
  getHistory(id: number, businessId: number): Observable<QuoteHistoryEntry[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteHistoryEntry[]>(`${this.apiUrl}/quotes/${id}/history`, { params });
  }

  // GET /api/quotes/metrics - KPI metrics (requires Manager+) (NEW)
  getMetrics(businessId: number): Observable<QuoteMetricsResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteMetricsResponse>(`${this.apiUrl}/quotes/metrics`, { params });
  }

  // GET /api/quotes/{id}/pdf - Download quote as PDF (NEW)
  downloadPdf(id: number, businessId: number): Observable<Blob> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get(`${this.apiUrl}/quotes/${id}/pdf`, { params, responseType: 'blob' });
  }

  // ---- Public portal endpoints (no JWT required) ----

  // GET /api/quotes/public/{token} - View quote by public token (NEW)
  getPublicQuote(token: string): Observable<QuotePublicResponse> {
    return this.http.get<QuotePublicResponse>(`${this.apiUrl}/quotes/public/${token}`);
  }

  // POST /api/quotes/public/{token}/sign - Sign quote electronically (NEW)
  signPublicQuote(token: string, signRequest: QuoteSignRequest): Observable<QuoteSignatureResponse> {
    return this.http.post<QuoteSignatureResponse>(`${this.apiUrl}/quotes/public/${token}/sign`, signRequest);
  }

  // POST /api/quotes/public/{token}/reject - Reject quote from the public portal (no JWT).
  // Solo válido en estados Publicada/Vista/En Revisión. 409 si ya fue aceptada/rechazada.
  rejectPublicQuote(token: string, rejectRequest: QuotePublicRejectRequest): Observable<QuotePublicRejectResponse> {
    return this.http.post<QuotePublicRejectResponse>(`${this.apiUrl}/quotes/public/${token}/reject`, rejectRequest);
  }

  // GET /api/quotes/public/{token}/pdf - Download PDF by public token (NEW)
  downloadPublicPdf(token: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/quotes/public/${token}/pdf`, { responseType: 'blob' });
  }
}
